import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { api, formatGBP, CATEGORIES } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const CATEGORY_IMAGES = {
  wigs: "https://images.unsplash.com/photo-1745975980824-bc88bd400c78?w=1200",
  bundles: "https://images.unsplash.com/photo-1527203561188-dae1bc1a417f?w=1200",
  frontals: "https://images.unsplash.com/photo-1668815142412-c912ff69ae15?w=1200",
  closures: "https://images.unsplash.com/photo-1521577352947-9bb58764b69a?w=1200",
  "hair-care": "https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=1200",
  accessories: "https://images.unsplash.com/photo-1604881991720-f91add269bed?w=1200",
};

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data));
  }, []);

  return (
    <div className="bg-[#FFF4E0]" data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[88vh]">
          <div className="lg:col-span-6 flex items-center px-6 md:px-12 lg:px-20 py-20 lg:py-0 order-2 lg:order-1">
            <div className="max-w-xl fade-in">
              <div className="overline mb-6">Est. 2026 · United Kingdom</div>
              <h1 className="font-serif-display text-[#1A0F11] text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight">
                Crown<br />
                <span className="italic text-[#831132]">yourself.</span>
              </h1>
              <p className="mt-8 text-[#5C4A4D] text-base md:text-lg max-w-md leading-relaxed">
                A curated house of premium wigs, bundles and crowns — hand-selected for the modern woman who refuses to dim her shine.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link to="/shop" className="btn-primary" data-testid="hero-shop-cta">
                  Shop the Edit <ArrowRight size={16} />
                </Link>
                <Link to="/about" className="btn-outline" data-testid="hero-story-cta">
                  Our Story
                </Link>
              </div>
              <div className="mt-12 flex items-center gap-8 text-xs text-[#5C4A4D] uppercase tracking-[0.2em]">
                <div>100% Virgin Hair</div>
                <div className="w-px h-4 bg-[#E8D8C8]" />
                <div>HD Lace</div>
                <div className="w-px h-4 bg-[#E8D8C8]" />
                <div>Worldwide</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 relative order-1 lg:order-2 min-h-[60vh] lg:min-h-[88vh]">
            <img
              src="https://images.unsplash.com/photo-1632765854612-9b02b6ec2b15?w=1600"
              alt="Crown yourself"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#831132]/30 via-transparent to-transparent" />
            <div className="absolute bottom-8 right-8 text-[#FFF4E0] text-right">
              <div className="overline text-[#FFF4E0]">A new house</div>
              <div className="font-serif-accent italic text-2xl mt-1">Luxury, redefined.</div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="bg-[#831132] text-[#FFF4E0] py-5 marquee">
        <div className="marquee-track font-serif-accent italic text-2xl md:text-3xl">
          <span>Crown yourself</span><span>·</span>
          <span>Premium raw donor hair</span><span>·</span>
          <span>HD lace, hand-tied</span><span>·</span>
          <span>Worldwide delivery</span><span>·</span>
          <span>Crown yourself</span><span>·</span>
          <span>Premium raw donor hair</span><span>·</span>
          <span>HD lace, hand-tied</span><span>·</span>
          <span>Worldwide delivery</span><span>·</span>
        </div>
      </section>

      {/* CATEGORIES — Bento */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline mb-3">The Collection</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A0F11]">Curated categories.</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex text-sm uppercase tracking-[0.22em] text-[#831132] hover:text-[#6A0D28]" data-testid="categories-shop-all">
            Shop all →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {CATEGORIES.map((c, i) => (
            <Link
              key={c.slug}
              to={`/shop/${c.slug}`}
              className={`relative group overflow-hidden hover-zoom ${
                i === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-[3/4]"
              }`}
              data-testid={`category-card-${c.slug}`}
            >
              <img src={CATEGORY_IMAGES[c.slug]} alt={c.label} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A0F11]/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 text-[#FFF4E0]">
                <div className="overline text-[#FFF4E0]/80">Shop</div>
                <div className="font-serif-display text-2xl md:text-3xl mt-1">{c.label}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 pb-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="overline mb-3">House favourites</div>
            <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A0F11]">The Crown List.</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
          {featured.slice(0, 8).map((p) => (
            <ProductCard key={p.product_id} product={p} />
          ))}
        </div>
      </section>

      {/* EDITORIAL */}
      <section className="bg-[#FDF1EC] py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <img src="https://images.unsplash.com/photo-1539701938214-0d9736e1c16b?w=1200" alt="Editorial" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="overline mb-4">Our story</div>
            <h2 className="font-serif-display text-4xl md:text-6xl text-[#1A0F11] leading-tight">
              Born in <em className="italic text-[#831132]">London</em>. Made for <em className="italic text-[#831132]">Lagos</em>.
            </h2>
            <p className="mt-6 text-[#5C4A4D] leading-relaxed max-w-lg">
              The Hair Bu&apos;tik was founded by Yetunde Olatunde with a single vision — to give women a premium hair house that treats their crown with the reverence it deserves. Hand-selected, ethically sourced, and obsessively quality-checked.
            </p>
            <Link to="/about" className="btn-outline mt-8" data-testid="story-cta">
              Read the journal
            </Link>
          </div>
        </div>
      </section>

      {/* LOOKBOOK */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="overline mb-3 text-center">#CrownedByHairButik</div>
        <h2 className="font-serif-display text-4xl md:text-5xl text-[#1A0F11] text-center mb-12">Lookbook.</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          {[
            "https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=900",
            "https://images.unsplash.com/photo-1668815142412-c912ff69ae15?w=900",
            "https://images.unsplash.com/photo-1527203561188-dae1bc1a417f?w=900",
            "https://images.unsplash.com/photo-1539701938214-0d9736e1c16b?w=900",
            "https://images.unsplash.com/photo-1632765854612-9b02b6ec2b15?w=900",
            "https://images.unsplash.com/photo-1745975980824-bc88bd400c78?w=900",
            "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=900",
            "https://images.unsplash.com/photo-1521577352947-9bb58764b69a?w=900",
          ].map((src, idx) => (
            <div key={idx} className={`overflow-hidden hover-zoom ${idx === 0 || idx === 5 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-auto" : "aspect-square"}`}>
              <img src={src} alt={`look-${idx}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
